<template>
  <div v-if="authenticated">
    <TransactionAdvancedSearch
      v-if="hasSearchTransactionsPermissions"
      v-model="searchModel"
      :statusOptions="transactionStatusArray"
      @filterSelectedStatus="filteredStatuses = $event"
      @search="searchEvent"
      @reset="resetSearch"
      :currentStatuses="currentStatuses"
      title="Advanced Search"
    >
      <template v-slot:additionalFields>
        <v-switch
          v-model="displayDeclinedTransactions"
          label="Show Declined Transactions"
        />
      </template>
    </TransactionAdvancedSearch>

    <b-row>
      <b-col v-if="hasCollectFundsPermission">
        <b-button
          class="full-width"
          @click="collectFunds(selected)"
          type="button"
          variant="primary"
          :disabled="disableCollectFundsOperation"
          >Collect Funds</b-button
        >
      </b-col>
      <b-col v-if="hasCollectFundsPermission">
        <b-button
          class="full-width"
          @click="confirmReverseFunds(selected)"
          type="button"
          variant="primary"
          :disabled="disableReverseFundsOperation"
          >Reversal</b-button
        >
      </b-col>
      <b-col v-if="hasRefundTransactionsPermissions">
        <b-button
          class="full-width"
          @click="confirmRefundTransactions(selected)"
          type="button"
          variant="danger"
          :disabled="disableRefundTransactionsOperation"
          >Refund</b-button
        >
      </b-col>
      <b-col v-if="hasExportTransactionsPermissions">
        <b-button
          class="full-width"
          @click="openExportTransactionsModal"
          type="button"
          variant="primary"
          >Export</b-button
        >
      </b-col>
      <b-col class="text-right pr-0">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              @click="searchEvent"
              color="blue"
              icon
              v-bind="attrs"
              v-on="on"
            >
              <v-icon size="30">refresh</v-icon>
            </v-btn>
          </template>
          <span>Refresh Table</span>
        </v-tooltip>
      </b-col>
      <b-col :cols="quickSearchCols">
        <b-input v-model="quickSearch" placeholder="Quick Search...." />
      </b-col>
    </b-row>
    <div v-if="isCustomerIdSet" class="full-width">
      <div class="d-flex justify-content-between align-items-center w-50 mt-5">
        <h3>Showing Transactions for: {{ searchModel.customerId }}</h3>
        <b-button variant="danger" @click="resetAndSearch">Show All</b-button>
      </div>
    </div>
    <b-row>
      <b-col>
        <TransactionsTable
          :search="quickSearch"
          :transactions="transactions"
          :transactionStatuses="transactionStatuses"
          :filteredStatuses="filteredStatuses"
          :loading="loading"
          :displayDeclinedTransactions="displayDeclinedTransactions"
          v-model="selected"
          @updateTableStatuses="currentStatuses = $event"
          ref="transactionsTable"
          @saveModification="saveAmountModification"
          @loadMoreTransaction="loadMoreTransactions"
          @viewTransactionHistory="openTransactionHistoryModal"
          @customerIdSearch="customerIdSearch"
        />
      </b-col>
    </b-row>
    <TransactionHistoryModal
      v-model="transactionHistoryModalModel"
      :dialog="transactionHistoryDialog"
      :transactionStatuses="transactionStatuses"
      :tenantAlias="tenantAlias"
      @close="closeTransactionHistoryModal"
    />
    <ExportTransactionsModal
      v-if="hasExportTransactionsPermissions"
      :isShowModal="isShowExportTransactionsModal"
      @eCloseModal="closeExportTransactionsModal"
    />
    <PartialRefundModal
      :dialog="partialRefundConfig.dialog"
      v-model="partialRefundConfig.model"
      @dismiss="closePartialRefundModal"
    />
    <LoadingModal v-bind="loadingModal" />
    <AlertModal
      v-bind="alertModal"
      v-model="alertModal.show"
      @dismiss="resetAlert"
    />
    <ConfirmationModal
      v-bind="confirmationModal"
      v-model="confirmationModal.show"
      @dismiss="resetConfirmation"
    />
  </div>
</template>

<script>
import TransactionsTable from "@/components/tables/TransactionsTable.vue";
import TransactionHistoryModal from "@/components/widgets/TransactionHistoryModal.vue";
import PartialRefundModal from "@/components/widgets/PartialRefundModal.vue";
import TransactionAdvancedSearch from "@/components/transaction/TransactionAdvancedSearch.vue";
import TransactionSearchMixin from "@/components/transaction/TransactionSearchMixin.js";
import TransactionPermissionsMixin from "@/components/transaction/TransactionPermissionsMixin.js";
import ExportTransactionsModal from "@/components/widgets/ExportTransactionsModal.vue";
import AlertModalMixin from "@/components/widgets/AlertModalMixin.js";
import { API, graphqlOperation } from "aws-amplify";
import { SET_BREADCRUMB } from "@/store/breadcrumbs.module";

import KTPortlet from "@/views/partials/content/Portlet";
import DateRange from "@/components/form-elements/search-components/DateRange.vue";
import MultiSelect from "@/components/form-elements/search-components/MultiSelect.vue";

const parseToFloat = TransactionSearchMixin.parseToFloat;

const AlertModal = AlertModalMixin.AlertModal;
const LoadingModal = AlertModalMixin.LoadingModal;
const ConfirmationModal = AlertModalMixin.ConfirmationModal;

const MODIFICATION_OPERATION = {
  Capture: "Captured",
  Reversal: "Reversal",
  Refund: "Refund"
};

const FULL_REFUND = "FULL REFUND";
const PARTIAL_REFUND = "PARTIAL REFUND";

export default {
  mixins: [
    TransactionSearchMixin,
    TransactionPermissionsMixin,
    AlertModalMixin
  ],
  components: {
    TransactionsTable,
    TransactionHistoryModal,
    ExportTransactionsModal,
    PartialRefundModal,
    TransactionAdvancedSearch
  },
  data() {
    return {
      filteredStatuses: [],
      isShowExportTransactionsModal: false,
      currentStatuses: [],
      selected: [],
      transactionHistoryDialog: false,
      transactionHistoryModalModel: {},
      partialRefundConfig: {
        dialog: false,
        model: {
          amount: 0.0
        },
        defaultItem: {
          amount: 0.0
        },
        callback: null
      },
      quickSearchCols: 4
    };
  },
  computed: {
    disableSingleItemOperations() {
      return !!this.selected && this.selected.length != 1;
    },
    disableCollectFundsOperation() {
      if (this.disableSingleItemOperations) return true;
      const selectedStatus = this.selected[0].transactionStatus;
      return !(
        !!selectedStatus &&
        !!this.transactionStatuses[selectedStatus] &&
        !!this.transactionStatuses[selectedStatus].capture
      );
    },
    disableReverseFundsOperation() {
      if (this.disableSingleItemOperations) return true;
      const selectedStatus = this.selected[0].transactionStatus;
      return !(
        !!selectedStatus &&
        !!this.transactionStatuses[selectedStatus] &&
        !!this.transactionStatuses[selectedStatus].reversal
      );
    },
    disableRefundTransactionsOperation() {
      if (this.disableSingleItemOperations) return true;
      const selectedStatus = this.selected[0].transactionStatus;
      return !(
        !!selectedStatus &&
        !!this.transactionStatuses[selectedStatus] &&
        (!!this.transactionStatuses[selectedStatus].partialRefund ||
          !!this.transactionStatuses[selectedStatus].refund)
      );
    }
  },
  methods: {
    collectFunds(items) {
      if (this.disableCollectFundsOperation || !this.hasCollectFundsPermission)
        return;
      this.$refs.transactionsTable.editItem(items[0]);
    },
    async confirmReverseFunds(items) {
      if (this.disableReverseFundsOperation || !this.hasCollectFundsPermission)
        return;
      const item = items[0];
      const confirm = await this.confirm(
        `Reverse Transaction?`,
        `Are you sure you want to reverse transaction ${item.orderNumber} - ${item.firstName} ${item.lastName}?`,
        ConfirmationModal.WARNING
      );
      if (!confirm) return;
      await this.reverseFunds(item);
    },
    async reverseFunds(item) {
      const index = this.transactions.findIndex(
        t => t.transactionId == item.transactionId
      );
      item.amount = 0;
      const modificationOperation = MODIFICATION_OPERATION.Reversal;

      let reversalResponseMessage = "Transaction Reversed Successfully";
      let alertType = AlertModal.SUCCESS;

      this.startLoadingDialog(`Reversing transaction: ${item.orderNumber}`);

      try {
        const response = await this.modifyTransaction(
          modificationOperation,
          item
        );

        if (!response.Successful) {
          reversalResponseMessage = "Transaction Reversed Unsuccessful";
          alertType = AlertModal.ERROR;
        }

        this.updateItemTransactionStatus(response.TransactionStatus, index);
      } catch (e) {
        this.$logger.error(e);
        reversalResponseMessage = this.handleTransModificationFailure(
          "Reversal",
          "There was an error reversing the transaction\nPlease click the refresh button.",
          e,
          index
        );
        alertType = AlertModal.ERROR;
      }
      this.stopLoadingDialog(reversalResponseMessage, alertType);
      this.uncheckItem(item.transactionId);
    },
    async confirmRefundTransactions(items) {
      if (
        this.disableRefundTransactionsOperation ||
        !this.hasRefundTransactionsPermissions
      )
        return;
      const item = items[0];
      const status = item.transactionStatus;
      if (!!this.transactionStatuses[status].partialRefund) {
        const modifiedItem = await this.openPartialRefundModal(item);
        if (!modifiedItem) return;
        modifiedItem.amount = this.formatAmount(modifiedItem.amount);
        const confirmationTitle = "Refund Transaction?";
        const confirmationMessage = `Are you sure you want to refund $${modifiedItem.amount} from transaction ${item.orderNumber} - ${item.firstName} ${item.lastName}?`;
        const confirm = await this.confirm(
          confirmationTitle,
          confirmationMessage,
          ConfirmationModal.WARNING
        );
        if (!confirm) return;
        await this.partialRefundTransaction(item, modifiedItem);
        return;
      }
      const confirmationTitle = "Refund Transaction?";
      const confirmationMessage = `Are you sure you want to refund transaction ${item.orderNumber} - ${item.firstName} ${item.lastName}?`;
      const confirm = await this.confirm(
        confirmationTitle,
        confirmationMessage,
        ConfirmationModal.WARNING
      );
      if (!confirm) return;
      await this.refundTransaction(item);
    },
    openPartialRefundModal(item) {
      return new Promise(resolve => {
        this.partialRefundConfig.model = Object.assign({}, item);
        this.partialRefundConfig.callback = resolve;
        this.partialRefundConfig.dialog = true;
      });
    },
    closePartialRefundModal(result) {
      this.partialRefundConfig.dialog = false;
      this.$nextTick(() => {
        this.partialRefundConfig.model = Object.assign(
          {},
          this.partialRefundConfig.defaultItem
        );
      });
      if (!!this.partialRefundConfig.callback) {
        const callback = this.partialRefundConfig.callback;
        this.partialRefundConfig.callback = null;
        callback(result);
      }
    },
    openExportTransactionsModal() {
      this.isShowExportTransactionsModal = true;
    },
    closeExportTransactionsModal() {
      this.isShowExportTransactionsModal = false;
    },
    async partialRefundTransaction(oItem, mItem) {
      const refundType = this.partialRefundAmountCheck(
        oItem.amount,
        mItem.amount
      );

      if (!refundType) return;

      if (refundType === FULL_REFUND)
        return await this.refundTransaction(oItem);

      const index = this.transactions.findIndex(
        t => t.transactionId == oItem.transactionId
      );

      const modificationOperation = MODIFICATION_OPERATION.Refund;

      let partialRefundResponseMessage = "Partial Refund Successfully";
      let alertType = AlertModal.SUCCESS;

      const newItem = { ...oItem };
      newItem.amount = this.formatAmount(
        parseToFloat(oItem.amount) - parseToFloat(mItem.amount)
      );
      Object.assign(this.transactions[index], newItem);

      this.startLoadingDialog(
        `Refunding $${mItem.amount} of transaction: ${mItem.orderNumber}`
      );

      try {
        const response = await this.modifyTransaction(
          modificationOperation,
          mItem
        );

        if (!response.Successful) {
          partialRefundResponseMessage = "Partial Refund Unsuccessful";
          alertType = AlertModal.ERROR;
          Object.assign(this.transactions[index], oItem);
        }

        this.updateItemTransactionStatus(response.TransactionStatus, index);
      } catch (e) {
        this.$logger.error(e);
        partialRefundResponseMessage = this.handleTransModificationFailure(
          "Partial Refund",
          "There was an error refunding the transaction\nPlease click the refresh button.",
          e,
          index
        );
        alertType = AlertModal.ERROR;
      }
      this.stopLoadingDialog(partialRefundResponseMessage, alertType);
      this.uncheckItem(oItem.transactionId);
    },
    async refundTransaction(item) {
      const index = this.transactions.findIndex(
        t => t.transactionId == item.transactionId
      );

      const modificationOperation = MODIFICATION_OPERATION.Refund;

      let refundResponseMessage = "Transaction Refund Successfully";
      let alertType = AlertModal.SUCCESS;

      this.startLoadingDialog(`Refunding transaction: ${item.orderNumber}`);

      try {
        const response = await this.modifyTransaction(
          modificationOperation,
          item
        );

        if (!response.Successful) {
          refundResponseMessage = "Transaction Refund Unsuccessful";
          alertType = AlertModal.ERROR;
        }

        this.updateItemTransactionStatus(response.TransactionStatus, index);
      } catch (e) {
        this.$logger.error(e);
        refundResponseMessage = this.handleTransModificationFailure(
          "Refund",
          "There was an error refunding the transaction\nPlease click the refresh button.",
          e,
          index
        );
        alertType = AlertModal.ERROR;
      }
      this.stopLoadingDialog(refundResponseMessage, alertType);
      this.uncheckItem(item.transactionId);
    },
    saveAmountModification(editedIndex, editedItem) {
      editedItem.amount = this.formatAmount(editedItem.amount);
      const originalItem = { ...this.transactions[editedIndex] };
      const modificationOperation = this.collectFundsAmountCheck(
        originalItem.amount,
        editedItem.amount
      );
      if (!modificationOperation) return;
      Object.assign(this.transactions[editedIndex], editedItem);
      this.startLoadingDialog(
        `Collecting funds for Order: ${originalItem.orderNumber}`
      );

      let captureResponseMessage = "Funds Collected Successfully";
      let alertType = AlertModal.SUCCESS;

      this.modifyTransaction(modificationOperation, editedItem)
        .then(response => {
          if (!response.Successful) {
            captureResponseMessage = "Fund Collection Unsuccessful";
            alertType = AlertModal.ERROR;
            Object.assign(this.transactions[editedIndex], originalItem);
          }

          this.updateItemTransactionStatus(
            response.TransactionStatus,
            editedIndex
          );
          this.stopLoadingDialog(captureResponseMessage, alertType);
        })
        .catch(e => {
          this.$logger.error(e);
          captureResponseMessage = this.handleTransModificationFailure(
            "Fund Collection",
            "There was an error collecting the funds\nPlease click the refresh button.",
            e,
            editedIndex
          );
          alertType = AlertModal.ERROR;
        })
        .finally(() => {
          this.stopLoadingDialog(captureResponseMessage, alertType);
          this.uncheckItem(editedItem.transactionId);
        });
    },
    collectFundsAmountCheck(oAmount, mAmount) {
      const originalAmount = parseToFloat(oAmount);
      const modifiedAmount = parseToFloat(mAmount);
      let warningMessage = `You cannot collect an amount greater than $${oAmount} for this transaction.`;
      if (originalAmount == modifiedAmount)
        return MODIFICATION_OPERATION.Capture;
      else if (originalAmount > modifiedAmount) {
        if (this.hasPartialCollectFundsPermissions)
          return MODIFICATION_OPERATION.Reversal;
        else
          warningMessage = `You do not have the required permissions to collect an amount less than $${oAmount} for this transaction.`;
      }
      this.alert(warningMessage, AlertModal.WARNING);
      return null;
    },
    partialRefundAmountCheck(oAmount, mAmount) {
      const originalAmount = parseToFloat(oAmount);
      const modifiedAmount = parseToFloat(mAmount);
      let warningMessage = `You cannot refund an amount greater than $${oAmount} for this transaction.`;
      if (originalAmount == modifiedAmount) return FULL_REFUND;
      else if (originalAmount > modifiedAmount) {
        return PARTIAL_REFUND;
      }
      this.alert(warningMessage, AlertModal.WARNING);
      return null;
    },
    updateItemTransactionStatus(newStatus, index) {
      const itemIndex = index;
      this.transactions[itemIndex].transactionStatus = newStatus;
    },
    async modifyTransaction(ModificationOperation, item) {
      const modifyTransaction = `
              mutation ModifyTransaction(
                $body: MutationModifyTransactionBodyInput
              ) {
                modifyTransaction(body: $body){
                  TransactionStatus,
                  Successful
                }
              }
            `;
      const TenantAlias = this.tenantAlias;
      const TransactionId = item.transactionId;
      const NewAmount = parseToFloat(item.amount);

      const modificationRequest = {
        ModificationRequest: {
          TenantAlias,
          TransactionId,
          NewAmount,
          ModificationOperation
        }
      };

      const modificationResponse = await API.graphql(
        graphqlOperation(modifyTransaction, { body: modificationRequest })
      );
      return modificationResponse.data.modifyTransaction;
    },
    uncheckItem(transactionId) {
      if (this.selected.length < 1) return;
      const index = this.selected.findIndex(
        s => s.transactionId === transactionId
      );
      this.selected.splice(index, 1);
    },
    handleTransModificationFailure(
      modificationTitle,
      defaultErrorMessage,
      e,
      transactionIndex
    ) {
      try {
        if (!this.isTimeoutException(e)) return defaultErrorMessage;
        const newStatus = `${modificationTitle}Requested`;
        this.updateItemTransactionStatus(newStatus, transactionIndex);
        return `${modificationTitle} took too long to respond.\n Please click the refresh button to see the updated status.`;
      } catch (e) {
        this.$logger.error(e);
      }
      return defaultErrorMessage;
    },
    isTimeoutException(e) {
      const EXECUTION_TIMEOUT = "ExecutionTimeout";
      return e.errors.findIndex(e => e.errorType === EXECUTION_TIMEOUT) !== -1;
    },
    openTransactionHistoryModal(item) {
      this.transactionHistoryModalModel = Object.assign({}, item);
      this.transactionHistoryDialog = true;
    },
    closeTransactionHistoryModal() {
      this.transactionHistoryDialog = false;
      this.$nextTick(() => {
        this.transactionHistoryModalModel = {};
      });
    }
  },
  async created() {
    try {
      await this.searchEvent();
    } catch (e) {
      this.$logger.error(error);
    }
  },
  mounted() {
    this.refreshTableHeaders = this.$refs.transactionsTable.refreshHeaders;
    this.$store.dispatch(SET_BREADCRUMB, [{ title: "Transactions" }]);
  }
};
</script>

<style lang="scss" scoped>
.full-width {
  width: 100%;
}
</style>
